import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Typography,
    TextField,
    InputAdornment,
    Button,
    Alert,
    Skeleton,
    Tooltip,
    TableSortLabel,
} from '@mui/material';
import { Search, History, ArrowForward } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { WarehouseAPI } from '../services/api';
import { CardingMoveLog } from '../types';
import TablePaginationBar, { DEFAULT_PAGE_SIZE } from '../components/common/TablePaginationBar';

type SortKey = 'when' | 'user' | 'sku' | 'qty' | 'status';

const MotionTableRow = motion(TableRow);

const statusColor: Record<CardingMoveLog['status'], { bg: string; color: string }> = {
    pending: { bg: '#fef9c3', color: '#a16207' },
    simulated: { bg: '#FAFAF7', color: '#4A433D' },
    synced: { bg: '#dcfce7', color: '#15803d' },
    failed: { bg: '#fee2e2', color: '#b91c1c' },
};

const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
    });
};

const CardingMoveLogsTab: React.FC = () => {
    const [logs, setLogs] = useState<CardingMoveLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Client-side sort (default newest-first, matching the previous implicit order) and page.
    // Both run over the FULL filtered list below, then the page is sliced out — so sorting
    // reorders every row, not just the visible page.
    const [sortKey, setSortKey] = useState<SortKey>('when');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
    const [page, setPage] = useState(0);

    const handleSort = (key: SortKey) => {
        if (sortKey === key) { setSortDir(d => (d === 'asc' ? 'desc' : 'asc')); }
        else { setSortKey(key); setSortDir(key === 'when' || key === 'qty' ? 'desc' : 'asc'); }
    };

    // Stored in the URL ("l" prefix to avoid colliding with other Carding sub-tabs) so a
    // refresh restores the same search instead of clearing it.
    const [searchParams, setSearchParams] = useSearchParams();
    const searchQuery = searchParams.get('lq') || '';
    const setSearchQuery = (v: string) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (v) next.set('lq', v); else next.delete('lq');
            return next;
        }, { replace: true });
    };

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await WarehouseAPI.getCardingMoves();
            setLogs(data);
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'Failed to load move logs');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchLogs(); }, [fetchLogs]);

    const filtered = useMemo(() => {
        if (!searchQuery.trim()) return logs;
        const q = searchQuery.trim().toLowerCase();
        return logs.filter(l =>
            l.skuCode.toLowerCase().includes(q) ||
            l.fromShelf.toLowerCase().includes(q) ||
            l.toShelf.toLowerCase().includes(q) ||
            (l.performedBy || '').toLowerCase().includes(q) ||
            (l.performedByName || '').toLowerCase().includes(q)
        );
    }, [logs, searchQuery]);

    // Sort the FULL filtered list, then slice the page below — never sort just the page.
    const sorted = useMemo(() => {
        const dir = sortDir === 'asc' ? 1 : -1;
        const arr = [...filtered];
        arr.sort((a, b) => {
            switch (sortKey) {
                case 'when':
                    return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * dir;
                case 'user':
                    return (a.performedByName || a.performedBy || '').localeCompare(b.performedByName || b.performedBy || '') * dir;
                case 'sku':
                    return a.skuCode.localeCompare(b.skuCode) * dir;
                case 'qty':
                    return (a.quantity - b.quantity) * dir;
                case 'status':
                    return a.status.localeCompare(b.status) * dir;
                default:
                    return 0;
            }
        });
        return arr;
    }, [filtered, sortKey, sortDir]);

    // Search is the only filter here — reset to the first page whenever it changes so results
    // aren't shown on a now-out-of-range page index.
    useEffect(() => { setPage(0); }, [searchQuery]);

    const paged = useMemo(
        () => sorted.slice(page * DEFAULT_PAGE_SIZE, page * DEFAULT_PAGE_SIZE + DEFAULT_PAGE_SIZE),
        [sorted, page]
    );

    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                {[1, 2, 3, 4].map(i => (
                    <Skeleton key={i} variant="rounded" height={56} sx={{ mb: 1, borderRadius: 2 }} />
                ))}
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" action={<Button onClick={fetchLogs}>Retry</Button>} sx={{ m: 2 }}>{error}</Alert>;
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    placeholder="Search by SKU, shelf, or user…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ minWidth: 280, bgcolor: '#ffffff' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Chip
                    icon={<History sx={{ fontSize: 16 }} />}
                    label={`${filtered.length} moves`}
                    size="small"
                    sx={{ fontWeight: 600, bgcolor: '#FAFAF7' }}
                />
            </Box>

            {filtered.length === 0 ? (
                <Alert severity="info">No carding moves recorded yet.</Alert>
            ) : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small" stickyHeader sx={{ minWidth: 900 }}>
                        <TableHead>
                            <TableRow sx={{ '& .MuiTableCell-head': { py: 1.5 } }}>
                                <TableCell sortDirection={sortKey === 'when' ? sortDir : false}>
                                    <TableSortLabel active={sortKey === 'when'} direction={sortKey === 'when' ? sortDir : 'desc'} onClick={() => handleSort('when')}>When</TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={sortKey === 'user' ? sortDir : false}>
                                    <TableSortLabel active={sortKey === 'user'} direction={sortKey === 'user' ? sortDir : 'asc'} onClick={() => handleSort('user')}>User</TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={sortKey === 'sku' ? sortDir : false}>
                                    <TableSortLabel active={sortKey === 'sku'} direction={sortKey === 'sku' ? sortDir : 'asc'} onClick={() => handleSort('sku')}>SKU</TableSortLabel>
                                </TableCell>
                                <TableCell>Move</TableCell>
                                <TableCell align="right" sortDirection={sortKey === 'qty' ? sortDir : false}>
                                    <TableSortLabel active={sortKey === 'qty'} direction={sortKey === 'qty' ? sortDir : 'desc'} onClick={() => handleSort('qty')}>Qty</TableSortLabel>
                                </TableCell>
                                <TableCell>Packaging</TableCell>
                                <TableCell>Remarks (sent to Unicommerce)</TableCell>
                                <TableCell sortDirection={sortKey === 'status' ? sortDir : false}>
                                    <TableSortLabel active={sortKey === 'status'} direction={sortKey === 'status' ? sortDir : 'asc'} onClick={() => handleSort('status')}>Status</TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <AnimatePresence initial={false}>
                                {paged.map((log, idx) => (
                                    <MotionTableRow
                                        key={log._id}
                                        hover
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={{ duration: 0.22, delay: Math.min(idx, 12) * 0.02, ease: 'easeOut' }}
                                    >
                                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.78rem', color: 'text.secondary' }}>
                                            {formatDate(log.createdAt)}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={log.performedBy || 'unknown'}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {log.performedByName || log.performedBy || 'Unknown'}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{log.skuCode}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Chip label={log.fromShelf} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} />
                                                <ArrowForward sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                <Chip label={log.toShelf} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} />
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
                                            {log.quantity.toLocaleString()}
                                        </TableCell>
                                        <TableCell>
                                            {(log.packagingDeductions || []).length === 0 ? (
                                                <Typography variant="caption" color="text.disabled">none</Typography>
                                            ) : (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    {log.packagingDeductions.map((pd, i) => (
                                                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.7rem' }}>
                                                            <Chip label={pd.packagingSku} size="small" sx={{ fontFamily: 'monospace', fontSize: '0.65rem', bgcolor: '#fef3c7' }} />
                                                            <ArrowForward sx={{ fontSize: 12, color: 'text.secondary' }} />
                                                            <Chip label={pd.destinationShelf || '—'} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }} />
                                                            <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>×{pd.quantityDeducted}</Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.8rem', fontStyle: 'italic', color: 'text.secondary' }}>
                                            {log.remarks || '—'}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={log.status === 'failed'
                                                ? `${log.failureReason || 'Unknown failure'}${log.unicommerceResponse ? ` | Raw response: ${JSON.stringify(log.unicommerceResponse)}` : ''}`
                                                : ''}>
                                                <Chip
                                                    label={log.status}
                                                    size="small"
                                                    sx={{
                                                        fontWeight: 600, textTransform: 'capitalize',
                                                        bgcolor: statusColor[log.status].bg,
                                                        color: statusColor[log.status].color,
                                                        cursor: log.status === 'failed' ? 'help' : 'default',
                                                    }}
                                                />
                                            </Tooltip>
                                        </TableCell>
                                    </MotionTableRow>
                                ))}
                            </AnimatePresence>
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <TablePaginationBar page={page} total={filtered.length} rowsPerPage={DEFAULT_PAGE_SIZE} onPageChange={setPage} />
        </Box>
    );
};

export default CardingMoveLogsTab;
